<script setup>
import { ref, computed, watch } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormText from '@/components/Atoms/FormText.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue'
import { FALLBACK_CATALOG, languageName } from './captionLanguages'
import {
	FORMALITY_OPTIONS,
	INSTRUCTIONS_MAX,
	GENDER_AWARE_NEEDS_SPEAKERS,
	COMPRESS_HELP,
	GENDER_AWARE_HELP,
	optionsFor,
	isCustomised,
	translationErrorsFor,
} from './captionTranslations'

/**
 * Per-language Gemini options, one tab per language. Edits a local copy of the
 * translation draft: Done hands it back (`apply`), Cancel or close drops it.
 * Nothing is sent from here; the panel's Save/Start commit it.
 */
const open = defineModel({ type: Boolean, default: false })

const props = defineProps({
	// The panel's translation draft `{ gemini, options }`, copied on open.
	draft: { type: Object, required: true },
	// Languages on Gemini, in tab order.
	targets: { type: Array, default: () => [] },
	// Tab to open on.
	initialLang: { type: String, default: '' },
	source: { type: String, default: '' },
	langs: { type: Array, default: () => [] },
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
	speakers: { type: Boolean, default: false },
	// Mapped server errors `{ [lang]: { [field]: { message, value } } }`.
	serverErrors: { type: Object, default: () => ({}) },
	disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['apply'])

const local = ref({ gemini: false, options: {} })
const active = ref('')

watch(open, (isOpen) => {
	if (!isOpen) return
	local.value = JSON.parse(JSON.stringify({ gemini: !!props.draft.gemini, options: props.draft.options || {} }))
	active.value = props.targets.includes(props.initialLang) ? props.initialLang : (props.targets[0] || '')
}, { immediate: true })

const name = (code) => languageName(code, props.catalog)
const single = computed(() => props.targets.length === 1)
const title = computed(() => (single.value ? `Translation options · ${name(props.targets[0])}` : 'Translation options'))

const errors = computed(() => translationErrorsFor(local.value, { source: props.source, langs: props.langs, catalog: props.catalog }, props.serverErrors))
const errorCount = (lang) => Object.keys(errors.value[lang] || {}).length
const fieldError = (lang, field) => errors.value[lang]?.[field] || ''
const opt = (lang) => optionsFor(local.value, lang)
const customised = (lang) => isCustomised(opt(lang), props.speakers)
const instructionsLength = (lang) => String(opt(lang).instructions || '').length

function setOption(lang, field, value) {
	const current = optionsFor(local.value, lang)
	local.value = { ...local.value, options: { ...(local.value.options || {}), [lang]: { ...current, [field]: value } } }
}

function done() {
	emit('apply', local.value.options || {})
	open.value = false
}
</script>

<template>
	<ModalBox v-model="open" size="sm" :title="title" scroll-body mobile-placement="bottom" body-classes="px-4 sm:px-6">
		<div class="flex flex-col gap-4 pb-4" data-testid="translation-modal">
			<TabGroup v-if="!single" type="lines" data-testid="translation-tabs">
				<TabItem
					v-for="lang in targets"
					:key="lang"
					type="line"
					:is-active="active === lang"
					:data-translation-lang="lang"
					:data-active="active === lang ? 'true' : undefined"
					data-testid="translation-tab"
					@click="active = lang"
				>
					<span class="inline-flex items-center gap-1.5">
						{{ name(lang) }}
						<span v-if="errorCount(lang)" class="rounded-full bg-error px-1.5 text-[10px] font-semibold leading-4 text-white" data-testid="translation-tab-errors">{{ errorCount(lang) }}</span>
						<span v-else-if="customised(lang)" class="size-1.5 rounded-full bg-main-light" data-testid="translation-tab-dot" aria-label="Customised" />
					</span>
				</TabItem>
			</TabGroup>

			<div
				v-for="lang in targets.filter((l) => l === active)"
				:key="lang"
				class="flex flex-col gap-4"
				:data-translation-lang="lang"
				data-testid="translation-options"
			>
				<p v-if="fieldError(lang, 'general')" class="m-0 text-xs text-error-light" role="alert">{{ fieldError(lang, 'general') }}</p>

				<div class="flex flex-col gap-1">
					<FormSelect
						:id="`translation-${lang}-formality`"
						:model-value="opt(lang).formality"
						label="Formality"
						:options="FORMALITY_OPTIONS"
						size="sm"
						:disabled="disabled"
						@update:model-value="setOption(lang, 'formality', $event)"
					/>
					<p v-if="fieldError(lang, 'formality')" class="m-0 text-xs text-error-light" role="alert">{{ fieldError(lang, 'formality') }}</p>
				</div>

				<div class="flex flex-col gap-1">
					<div class="flex items-center gap-1.5">
						<FormSwitch
							:id="`translation-${lang}-compress`"
							:model-value="opt(lang).compress"
							size="sm"
							label="Shorten long lines"
							label-styles="text-tiny font-medium text-surface-text-1"
							:disabled="disabled"
							@update:model-value="setOption(lang, 'compress', $event)"
						/>
						<Tooltip :content="COMPRESS_HELP" wrapper-class="inline-flex">
							<span class="inline-flex text-surface-7 hover:text-white" tabindex="0" aria-label="More info: condensing lines" data-testid="compress-help">
								<icon-base width="14" height="14" width-view-box="16" height-view-box="16"><icon-info-small /></icon-base>
							</span>
						</Tooltip>
					</div>
					<p v-if="fieldError(lang, 'compress')" class="m-0 text-xs text-error-light" role="alert">{{ fieldError(lang, 'compress') }}</p>
				</div>

				<div class="flex flex-col gap-1">
					<div class="flex items-center gap-1.5">
						<FormSwitch
							:id="`translation-${lang}-gender`"
							:model-value="speakers && opt(lang).genderAware"
							size="sm"
							label="Gender-aware"
							label-styles="text-tiny font-medium text-surface-text-1"
							:disabled="disabled || !speakers"
							@update:model-value="setOption(lang, 'genderAware', $event)"
						/>
						<Tooltip :content="GENDER_AWARE_HELP" wrapper-class="inline-flex">
							<span class="inline-flex text-surface-7 hover:text-white" tabindex="0" aria-label="More info: speaker gender" data-testid="gender-help">
								<icon-base width="14" height="14" width-view-box="16" height-view-box="16"><icon-info-small /></icon-base>
							</span>
						</Tooltip>
					</div>
					<p v-if="fieldError(lang, 'genderAware')" class="m-0 text-xs text-error-light" role="alert">{{ fieldError(lang, 'genderAware') }}</p>
					<p v-else-if="!speakers" class="m-0 text-xs text-surface-7" data-testid="gender-needs-speakers">{{ GENDER_AWARE_NEEDS_SPEAKERS }}</p>
				</div>

				<div class="flex flex-col gap-1">
					<div class="flex items-baseline justify-between gap-2">
						<label :for="`translation-${lang}-instructions`" class="m-0 text-tiny font-medium text-surface-text-1">Instructions</label>
						<span
							class="text-[11px] tabular-nums"
							:class="instructionsLength(lang) > INSTRUCTIONS_MAX ? 'text-error-light' : 'text-surface-7'"
							data-testid="instructions-count"
						>{{ instructionsLength(lang) }} / {{ INSTRUCTIONS_MAX }}</span>
					</div>
					<FormText
						:id="`translation-${lang}-instructions`"
						:model-value="opt(lang).instructions"
						size="lg"
						noresize
						:is-error="!!fieldError(lang, 'instructions')"
						:placeholder="`e.g. Keep product names in English; use “vous” in ${name(lang)}.`"
						:disabled="disabled"
						@update:model-value="setOption(lang, 'instructions', $event)"
						@keydown.enter.prevent
					/>
					<p v-if="fieldError(lang, 'instructions')" class="m-0 text-xs text-error-light" role="alert">{{ fieldError(lang, 'instructions') }}</p>
				</div>
			</div>
		</div>

		<template #footer>
			<div class="flex flex-col-reverse gap-2 border-t border-surface-3 p-4 sm:flex-row sm:justify-end sm:px-6">
				<FormButton type="secondary" size="md" class="justify-center" data-testid="translation-modal-cancel" @click="open = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="justify-center" :disabled="disabled" data-testid="translation-modal-done" @click="done">Done</FormButton>
				<p class="m-0 text-xs text-surface-7 sm:order-first sm:mr-auto sm:self-center" data-testid="translation-modal-note">Applies when you save or start captions.</p>
			</div>
		</template>
	</ModalBox>
</template>
